<template>
  <div>
    <v-toolbar color="grey lighten-3">
      <v-toolbar-title>{{ $t('Detailed cash count') }}</v-toolbar-title>
      <v-spacer />
      <v-toolbar-items class="align-center">
        <v-chip
          color="primary"
          label
          text-color="white"
        >{{ $t('Total') }}: {{ countedTotal | money }}</v-chip>
      </v-toolbar-items>
    </v-toolbar>
    <v-card
      class="counter"
      tile
    >
      <v-container
        :style="{ opacity: valuesGridOpacity }"
        class="pt-0 pa-1"
      >
        <v-card-text class="pb-2">
          <v-row wrap>
            <input type="hidden">
            <v-col
              v-for="(moneyType, moneyTypeKey) in ['coins', 'bills']"
              :key="moneyTypeKey"
              cols="6"
            >
              <h3 class="subheading grey--text font-weight-regular mb-2">{{ $t(helpers.capitalize(moneyType)) }}</h3>
              <v-dialog
                v-for="(moneyValue, moneyValueKey) in moneyKeys[currency][moneyType]"
                v-model="showChangeQtyKeyboard[moneyValue]"
                :key="moneyValueKey"
                eager
                width="300px"
              >
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <v-text-field
                      :label="moneyValue | money"
                      :value="countedValues[moneyValue]"
                      class="mb-2"
                      filled
                      hide-details
                      placeholder="0"
                      readonly
                      @focus="setQtyFocus(moneyValue)"
                    />
                  </div>
                </template>
                <v-card
                  class="grey lighten-5"
                  tile
                >
                  <v-card-text class="pt-4">
                    <v-row>
                      <v-col cols="12">
                        <numeric-keyboard
                          :clear-input="showChangeQtyKeyboard[moneyValue]"
                          :keyboard-class="`keyboardQty-${moneyTypeKey}-${moneyValueKey}`"
                          max-width="250px"
                          @clear="$set(countedValues, moneyValue, 0)"
                          @input="$set(countedValues, moneyValue, $event)"
                        >
                          <v-text-field
                            slot="input"
                            slot-scope="{ setKeyboardInput }"
                            :ref="`inputQty-${moneyValue}`"
                            :label="$t('Quantity of {0}', [currencyValue(moneyValue)])"
                            :value="countedValues[moneyValue]"
                            autocomplete="off"
                            hide-details
                            filled
                            @input="$set(countedValues, moneyValue, $event);setKeyboardInput($event)"
                            @keypress.enter="openNextValuePopup(moneyValue)"
                          />
                        </numeric-keyboard>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-divider />
                  <v-card-actions>
                    <v-btn
                      color="primary"
                      class="font-weight-bold"
                      text
                      @click="$set(showChangeQtyKeyboard, moneyValue, false)"
                    >{{ $t('Close') }}</v-btn>
                    <v-spacer />
                    <v-btn
                      class="font-weight-bold"
                      color="green"
                      text
                      @click="openNextValuePopup(moneyValue)"
                    >
                      <v-icon class="mr-1">fal fa-angle-double-right</v-icon>
                      {{ $t('Next') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider/>
        <v-slide-y-transition>
          <v-card-actions>
            <v-btn
              class="font-weight-bold"
              color="red"
              text
              @click="clearValues"
            >{{ $t('Clear') }}</v-btn>
            <v-spacer />
            <v-btn
              class="font-weight-bold"
              color="primary"
              text
              @click="$emit('saved', { countedValues, countedTotal })"
            >{{ $t('Save') }}</v-btn>
          </v-card-actions>
        </v-slide-y-transition>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import NumericKeyboard from '@/components/NumericKeyboard'
import { money } from '@/utils/filters'

export default {
  name: 'FormsCashRegistersDetailedCount',

  components: {
    NumericKeyboard
  },

  props: {
    currency: {
      type: String,
      default: 'EUR'
    }
  },

  data: () => ({
    countedTotal: 0,
    countedValues: {},
    moneyKeys: {
      EUR: {
        coins: [ 0.01, 0.02, 0.05, 0.10, 0.20, 0.50, 1, 2 ],
        bills: [ 5, 10, 20, 50, 100, 200, 500 ]
      }
    },
    showChangeQtyKeyboard: {},
    showFooter: true,
    valuesGridOpacity: 1
  }),

  computed: {
    // Get all possible moneykeys, merge coins and bills
    moneyKeysMerged () {
      const mergedMoneyKeys = {}
      for (let currency in this.moneyKeys) {
        mergedMoneyKeys[currency] = []
        for (let moneyType in this.moneyKeys[currency]) {
          for (let moneyValue of this.moneyKeys[currency][moneyType]) {
            mergedMoneyKeys[currency].push(moneyValue)
          }
        }
      }

      return mergedMoneyKeys
    }
  },

  watch: {
    countedValues: {
      deep: true,
      handler (val) {
        // Calculate the total monetary value
        const entries = Object.entries(this.countedValues)
        if (entries) {
          const countedTotal = entries.reduce(function (accumulator, [key, value]) {
            return accumulator + (key * value)
          }, 0)
          // EPSILON is the difference between 1 and the next number existing in the double precision floating point numbers.
          // Round to 2 decimals
          this.countedTotal = Math.round(countedTotal * 100 + Number.EPSILON) / 100
          this.persistToLocalStorage()
        }
      }
    },
    showChangeQtyKeyboard: {
      deep: true,
      handler (val) {
        // Check if at least one keyboard has been set to shown
        const result = Object.values(val).some((element) => element)
        this.showFooter = !result
        this.valuesGridOpacity = result ? 0.4 : 1
      }
    }
  },

  created () {
    // initialize counts, set each money value to count of 0
    this.moneyKeys[this.currency]['coins'].forEach((value) => {
      this.$set(this.countedValues, value, null)
    })
    this.moneyKeys[this.currency]['bills'].forEach((value) => {
      this.$set(this.countedValues, value, null)
    })
  },

  mounted () {
    this.readFromLocalStorage()
    this.$emit('mounted', { countedValues: this.countedValues, countedTotal: this.countedTotal })
  },

  methods: {
    clearValues () {
      this.countedValues = {}
    },
    currencyValue (moneyValue) {
      return money(moneyValue)
    },
    openNextValuePopup (currentMoneyValue) {
      // hide current popup
      this.$set(this.showChangeQtyKeyboard, currentMoneyValue, false)

      // show next popup
      const currentIndex = this.moneyKeysMerged[this.currency].findIndex(moneyValue => moneyValue === currentMoneyValue)
      const nextItem = this.moneyKeysMerged[this.currency][currentIndex + 1]
      if (nextItem) {
        this.$set(this.showChangeQtyKeyboard, nextItem, true)
        this.setQtyFocus(nextItem)
      }
    },
    persistToLocalStorage () {
      localStorage.closeCountedValues = JSON.stringify(this.countedValues)
      localStorage.closeCountedTotal = JSON.stringify(this.countedTotal)
    },
    readFromLocalStorage () {
      if (localStorage.closeCountedValues) {
        this.countedValues = JSON.parse(localStorage.closeCountedValues)
      }
      if (localStorage.closeCountedTotal) {
        this.countedTotal = JSON.parse(localStorage.closeCountedTotal)
      }
    },
    setQtyFocus (moneyValue) {
      setTimeout(() => {
        this.$nextTick(this.$refs[`inputQty-${moneyValue}`][0].focus)
      }, 300)
    }
  }
}
</script>

<style lang="scss" scoped>
  .counter /deep/ .v-label {
    font-size: 1.3em;
    font-weight: bold;
  }
  .v-menu {
    display: block !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/forms/cash-registers/FormsCashRegistersDetailedCount.vue